import { useEffect, useRef } from "react";
import { useRouter } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { getAuth, type RecaptchaVerifier } from "firebase/auth";
import { useSetAtom } from "jotai/react";
import { useForm } from "react-hook-form";

import { ScreenLayout } from "@/blocks/ScreenLayout";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { VnRecaptcha } from "@/components/VnRecaptcha";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import { useSendMfaVerificationCodeMutation } from "@/data/mutations/authMutations";
import { mfaPhoneFormSchema, MfaPhoneFormType } from "@/domain/auth";
import { MFA_VERIFY_ROUTE } from "@/domain/constants";
import { useTranslator } from "@/hooks/useTranslate";
import { createRecaptchaVerifier } from "@/services/firebase";
import { mfaVerificationIdAtom } from "@/stores/atoms";
import { logger } from "@/utils/logger";

const MfaPhoneScreen = () => {
  const { t } = useTranslator();
  const router = useRouter();
  const auth = getAuth();
  const recaptchaVerifier = useRef<RecaptchaVerifier | null>(null);
  const setMfaVerificationId = useSetAtom(mfaVerificationIdAtom);

  useEffect(() => {
    recaptchaVerifier.current ??= createRecaptchaVerifier(
      "recaptcha-container",
    );

    return () => {
      recaptchaVerifier.current?.clear();
      recaptchaVerifier.current = null;
    };
  }, [auth]);

  const { mutate: sendVerificationCode, isPending } =
    useSendMfaVerificationCodeMutation();

  const phoneForm = useForm<MfaPhoneFormType>({
    defaultValues: { phoneNumber: "" },
    resolver: standardSchemaResolver(mfaPhoneFormSchema),
  });

  const handleSubmit = (data: MfaPhoneFormType) => {
    if (!recaptchaVerifier.current) {
      logger.error({
        message: "Recaptcha verifier is not available",
        error: new Error("Recaptcha verifier is not available"),
      });
      return;
    }

    // Format phone number: prepend +1 if not already prefixed
    const formattedPhone = data.phoneNumber.startsWith("+")
      ? data.phoneNumber
      : `+1${data.phoneNumber.replace(/\D/g, "")}`;

    sendVerificationCode(
      {
        phoneNumber: formattedPhone,
        applicationVerifier: recaptchaVerifier.current,
      },
      {
        onSuccess: verificationId => {
          if (verificationId) {
            setMfaVerificationId(verificationId);
            router.push(MFA_VERIFY_ROUTE);
          } else {
            logger.error({
              message: "No verification ID returned",
              error: new Error("No verification ID returned"),
            });
            phoneForm.setError("phoneNumber", {
              message: t("common:validation.genericError"),
            });
          }
        },
        onError: error => {
          logger.error({
            message: "Failed to send MFA verification code",
            error,
          });
          phoneForm.setError("phoneNumber", {
            message: t("common:validation.genericError"),
          });
        },
      },
    );
  };

  return (
    <ScreenLayout className="py-12">
      <VnBox className="mt-safe-offset-10 web:mt-10 flex h-full items-center px-4 md:mt-0 md:justify-center">
        <VnBox className="w-full gap-4 md:w-3/6">
          <VnBox className="flex flex-col gap-6">
            <VnBox className="gap-2">
              <VnText
                variant="headerOne"
                className="text-center"
                i18nKey="native:mfaPhoneScreen.title"
              />
              <VnText
                variant="lg"
                className="text-muted-foreground text-center"
                i18nKey="native:mfaPhoneScreen.subtitle"
              />
            </VnBox>
            <VnForm form={phoneForm} isInputOnlyForm>
              <VnCard className="gap-3 rounded-2xl">
                <VnTextInput
                  control={phoneForm.control}
                  labelI18nKey="native:mfaPhoneScreen.phoneNumberLabel"
                  name="phoneNumber"
                  placeholderI18nKey="native:mfaPhoneScreen.phoneNumberPlaceholder"
                  leftElement={
                    <VnText variant="button" className="ml-2">
                      +1
                    </VnText>
                  }
                  inputClassName="pl-9"
                  autoCorrect={false}
                  keyboardType="phone-pad"
                  returnKeyType="done"
                  enablesReturnKeyAutomatically
                  onSubmitEditing={() => {
                    void phoneForm.handleSubmit(handleSubmit)();
                  }}
                  isRequired
                />
                <VnText
                  variant="sm"
                  className="text-muted-foreground"
                  i18nKey="native:mfaPhoneScreen.sendVerificationCodeText"
                />
                <VnButton
                  onPress={() => {
                    void phoneForm.handleSubmit(handleSubmit)();
                  }}
                  i18nKey="common:continue"
                  isLoading={isPending}
                  className="rounded-xl"
                />
              </VnCard>
            </VnForm>
          </VnBox>
        </VnBox>
      </VnBox>
      <VnRecaptcha />
    </ScreenLayout>
  );
};

export { MfaPhoneScreen };
